import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'hardware-fit', label: 'Hardware-fit verdict' },
  { id: 'privacy', label: 'Privacy & diagnostics' },
  { id: 'hints', label: 'Hints & tips' },
  { id: 'search-prefs', label: 'Search preferences' },
  { id: 'auto-update', label: 'Auto-update' },
  { id: 'install-panel', label: 'Install Premiere panel' },
  { id: 'export-folder', label: 'Export folder' },
  { id: 'storage', label: 'Storage (cache folder)' },
  { id: 'ultra', label: 'Ultra Search' },
  { id: 'licensing', label: 'Licensing' },
  { id: 'agent', label: 'Agent (MCP)' },
  { id: 'models', label: 'Models' },
  { id: 'system', label: 'System' },
  { id: 'panel-readonly', label: 'Read-only in the Premiere panel' },
];

export default function Body() {
  return (
    <>
      <DocSection id="hardware-fit" title="Hardware-fit verdict">
        <p>
          Settings is grouped by function rather than dumped into one long form. Every group
          auto-saves — a small "Saving…" / "Saved" word confirms it — and a{' '}
          <span style={kbd}>Refresh</span> button at the top re-checks your system status.
        </p>
        <p>
          Desktop only. The <strong>Hardware-fit verdict</strong> group runs the same hardware probe
          as the onboarding hardware check and shows a plain verdict for your machine — the exact
          minimums it's checking against live in{' '}
          <Link to="/docs/reference/system-requirements">System requirements</Link>. If your GPU can't
          run ClipRadar at all, or is tight on VRAM, this is the first place that says so; see{' '}
          <Link to="/docs/troubleshooting/gpu-not-detected">ClipRadar can't find my GPU</Link> if the
          verdict is red.
        </p>
      </DocSection>

      <DocSection id="privacy" title="Privacy & diagnostics">
        <DocShot
          slug="settings-tab"
          shot="privacy"
          alt="Settings — Privacy & diagnostics group with Send crash reports and Report an issue prompts toggles"
        />
        <p>
          Two toggles: <span style={kbd}>Send crash reports & diagnostics</span> gates whether crash
          data and bug-report uploads leave your machine at all — footage, file contents, names, and
          paths are never included, whatever this is set to; it takes effect on the next restart. The{' '}
          <span style={kbd}>"Report an issue?" prompts</span> toggle controls whether ClipRadar
          proactively offers to file a report after a repeated empty search, a failed analysis, or an
          Ultra fallback. See{' '}
          <Link to="/docs/concepts/local-and-private">Local & private</Link> for the exact boundary of
          what ever leaves this machine.
        </p>
      </DocSection>

      <DocSection id="hints" title="Hints & tips">
        <p>
          One global <span style={kbd}>Show hints & tips</span> switch. Turning it off silences
          reactive hint icons, proactive callouts, and the in-app guided tour all at once.
        </p>
      </DocSection>

      <DocSection id="search-prefs" title="Search preferences">
        <p>
          Controls that shape the <Link to="/docs/interface/search">Search tab</Link>:{' '}
          <span style={kbd}>Max results</span>, <span style={kbd}>Show duration</span>,{' '}
          <span style={kbd}>Show timestamps</span>, <span style={kbd}>Show coverage warnings in
          search</span>, and <span style={kbd}>Show recent searches</span>. A separate{' '}
          <span style={kbd}>Boost visual search with speech</span> toggle blends transcript matches
          into Visuals-mode ranking, useful when what's said and what's shown tend to line up in your
          footage.
        </p>
      </DocSection>

      <DocSection id="auto-update" title="Auto-update">
        <p>
          Desktop app only. Checks for, downloads, and applies updates, with a live download percent
          and a <span style={kbd}>Restart to update</span> button when one is ready. After an update
          lands, a what's-new dialog summarizes the release — the same notes live permanently on{' '}
          <Link to="/docs/release-notes">Release notes</Link>.
        </p>
      </DocSection>

      <DocSection id="install-panel" title="Install Premiere panel">
        <p>
          Desktop only. Reveals the bundled <span style={kbd}>.ccx</span> panel installer for a
          double-click install, plus a status readout once it's connected. The full illustrated install
          walkthrough — including the ZXP/UXP Installer fallback and how to open the panel in
          Premiere — lives on{' '}
          <Link to="/docs/platforms/premiere-panel">The Adobe Premiere Pro panel</Link>.
        </p>
      </DocSection>

      <DocSection id="export-folder" title="Export folder">
        <p>
          Where exported clips, timeline XML, and transcripts are written when you{' '}
          <Link to="/docs/interface/search#results-toolbar">export from the desktop app</Link>.{' '}
          <span style={kbd}>Browse</span> to change
          it. This is separate from the <strong>Cache folder</strong> below — the Export folder holds
          things you produce; the Cache folder holds ClipRadar's own analysis data. Full paths for both
          are in <Link to="/docs/reference/where-your-data-lives">Where ClipRadar stores things</Link>.
        </p>
      </DocSection>

      <DocSection id="storage" title="Storage (cache folder)">
        <p>
          The <strong>Cache folder</strong> holds every source's analysis data — thumbnails,
          embeddings, transcripts, face data. <span style={kbd}>Browse</span> to point it somewhere
          else, or <span style={kbd}>Reset to default</span>; moving it prompts whether to move the
          existing contents or leave them behind. Your original footage never lives here and never
          moves — see{' '}
          <Link to="/docs/reference/where-your-data-lives">Where ClipRadar stores things</Link>.
        </p>
      </DocSection>

      <DocSection id="ultra" title="Ultra Search">
        <DocShot
          slug="settings-tab"
          shot="ultra"
          alt="Settings — Ultra Search group with the query parser model picker, GPU memory policy, and VRAM readout"
        />
        <p>
          Everything that shapes how <Link to="/docs/concepts/ultra-search">Ultra Search</Link> runs on
          your machine: a <span style={kbd}>Query parser model</span> picker (each option shows its
          download size and state, with per-model <span style={kbd}>Download</span> /{' '}
          <span style={kbd}>Cancel</span>), a <span style={kbd}>GPU memory policy</span> —{' '}
          <span style={kbd}>Automatic</span>, <span style={kbd}>Keep ready</span>, or{' '}
          <span style={kbd}>Load on demand</span> — a{' '}
          <span style={kbd}>Remember filter edits per search</span> toggle, and a live readout of the
          parser's current state, VRAM usage, and tier (full vs. Lite). See{' '}
          <Link to="/docs/concepts/models-and-your-gpu">AI models & your GPU</Link> for what "Lite"
          means and why it depends on VRAM, not GPU presence.
        </p>
        <p>
          An <span style={kbd}>Advanced</span> section points Ultra's query parser at a custom
          OpenAI-compatible endpoint — LM Studio, Ollama, or similar — instead of the bundled model:
          endpoint URL, model name, and an optional API key. It's best-effort; if the endpoint isn't
          reachable, Ultra falls back to plain search rather than failing silently.
        </p>
      </DocSection>

      <DocSection id="licensing" title="Licensing">
        <p>
          Shows your current license state — no license, active, trial, or exhausted — with a masked
          key you can copy, an <span style={kbd}>Apply</span> field to activate a new key, and{' '}
          <span style={kbd}>Deactivate</span> to free this device's seat for use elsewhere. Trial and
          exhausted states also show a usage meter with a{' '}
          <span style={kbd}>Go unlimited</span> / <span style={kbd}>Upgrade</span> shortcut. Search
          being blocked because you're unlicensed or out of trial searches never surfaces as a raw
          error — see <Link to="/docs/concepts/licensing">Licensing & activation</Link> for the full
          picture, including trial limits and multi-device activation.
        </p>
      </DocSection>

      <DocSection id="agent" title="Agent (MCP)">
        <DocShot
          slug="settings-tab"
          shot="agent"
          alt="Settings — Agent (MCP) group with server status and a copyable connect command"
        />
        <p>
          Live status for ClipRadar's local MCP server, plus a one-time, copyable connect command (e.g.{' '}
          <span style={kbd}>claude mcp add …</span>) for pointing an AI agent — Claude Code, Claude
          Desktop, Codex — at it. Status reads as <span style={kbd}>Connected</span> (active or idle),{' '}
          <span style={kbd}>Connectable</span> with a tool count, or unreachable. Nothing here talks to
          the outside world: it's a local server, and the agent sees metadata, thumbnails, and
          transcripts — never your original footage files. Full concept and tool list:{' '}
          <Link to="/docs/concepts/agentic-editing">Agentic editing</Link> and{' '}
          <Link to="/docs/reference/mcp-tools">MCP tool reference</Link>.
        </p>
      </DocSection>

      <DocSection id="models" title="Models">
        <DocShot
          slug="settings-tab"
          shot="models"
          alt="Settings — Models group with per-model download status and the speech model picker"
        />
        <p>
          Download status for every model ClipRadar uses (Downloaded / Downloading / Failed with a{' '}
          <span style={kbd}>Retry</span>). The <strong>Visual model</strong> is fixed and read-only; the{' '}
          <strong>Speech model</strong> is a picker — Large v3 Turbo (the default), Large v3, Medium,
          Small, or Base — trading accuracy for speed and VRAM. See{' '}
          <Link to="/docs/reference/models">Machine-learning models</Link> for what each one is.
        </p>
        <p>
          <span style={kbd}>Transcript health → Check transcripts</span> runs a scan (not an analysis
          job) across your library, flags unreliable transcripts, and jumps you to Media's Problems
          filter to fix them — see{' '}
          <Link to="/docs/guides/fix-speech-problems">Fix speech & transcript problems</Link>. A{' '}
          <span style={kbd}>Speech device</span> readout shows live compute status for
          transcription, reflecting the required CUDA GPU rather than a setting you'd deliberately
          switch. Two sliders,{' '}
          <span style={kbd}>Scene merge threshold</span> and <span style={kbd}>Shot cut
          threshold</span>, tune how footage is split into scenes; changing either requires
          re-indexing to take effect.
        </p>
      </DocSection>

      <DocSection id="system" title="System">
        <p>
          <span style={kbd}>Compute device</span> confirms whether ClipRadar is running on your CUDA
          GPU (the only supported way to run it) alongside the installed{' '}
          <span style={kbd}>App version</span>. If this ever shows CPU outside of development or
          support diagnostics, something's wrong — start with{' '}
          <Link to="/docs/troubleshooting/gpu-not-detected">ClipRadar can't find my GPU</Link>.
        </p>
      </DocSection>

      <DocSection id="panel-readonly" title="Read-only in the Premiere panel">
        <Callout kind="warn">
          Inside the Premiere panel, the <strong>Export folder</strong>, <strong>Storage</strong>,{' '}
          <strong>Ultra Search</strong>, and <strong>Models</strong> groups are read-only, labeled
          "Read-only in the Premiere panel — change these in the ClipRadar desktop app." Everything
          under <strong>Search preferences</strong> and <strong>Licensing</strong> stays fully
          editable in both shells.
        </Callout>
        <p>
          The <strong>Hardware-fit verdict</strong> group is desktop-only and doesn't appear in the
          panel at all. This split exists because engine and filesystem settings apply to the one
          shared ClipRadar backend — changing them from inside Premiere would affect the desktop app
          too, so ClipRadar keeps that one source of truth in the desktop app.
        </p>
      </DocSection>
    </>
  );
}
